import { schema } from 'normalizr'
import get from 'lodash.get'

const handleEmpty = value => value || ''
const getNestedProp = (arr, prop) => {
    const dataObject = arr ? arr[0] : {}
    return get(dataObject, prop, '')
}

// expireMonth, expireYear: number
// returns string "MMYY" eg. "0120",
const getExpiryDate = (expireMonth, expireYear) =>
    `${expireMonth > 9 ? expireMonth : `0${expireMonth}`}${expireYear.toString().substr(-2)}`

export const vehicleSchema = (idAttribute = 'vehicleId') =>
    new schema.Entity(
        'vehicles',
        {},
        {
            idAttribute: value => value.fieldMap[idAttribute].value,
            processStrategy: ({
                fieldMap: { color, make, model, stateRegistered, state, dateAdded, vehicleId, plateNumber, isDeleted },
            }) => ({
                id: get(vehicleId, 'value', ''),
                state: get(stateRegistered || state, 'value', ''),
                color: get(color, 'value', ''),
                make: get(make, 'value', ''),
                model: get(model, 'value', ''),
                startDate: get(dateAdded, 'value', ''),
                tag: get(plateNumber, 'value', ''),
                active: '',
                name: '',
                default: '',
                sourceKey: '',
                isDeleted: isDeleted?.value || false,
            }),
        }
    )

export const extendedSchema = new schema.Object({
    fieldMap: {
        vehicles: {
            fieldList: [vehicleSchema()],
        },
    },
})

export const accountCouponSchema = new schema.Entity('coupons', undefined, {
    idAttribute: value => value.couponCode,
})

export const creditCardsSchema = new schema.Entity(
    'creditCards',
    {},
    {
        processStrategy: nAmFields => ({
            // All fields received from Thinkx API must be sent back
            // in exact same form when doing some edit request.
            nAmFields,
            id: nAmFields.id,
            lastFour: nAmFields.lastFour,
            expireMonth: nAmFields.expireMonth,
            expireYear: nAmFields.expireYear,
            paymentMethodType: nAmFields.paymentMethodType,
            cardType: nAmFields.cardType,
            token: nAmFields.token,
            bin: nAmFields.bin,
            cardLast4: `xxxx-xxxx-xxxx-${nAmFields.lastFour}`,
            expDate: getExpiryDate(nAmFields.expireMonth, nAmFields.expireYear),
            nickName: nAmFields.nameOnCard,
            default: nAmFields.defaultInd,
            accountLoginId: '',
            name: '',
            active: '',
            startDate: '',
            cvv: '',
            cardProcessor: '',
            sourceKey: '',
            cardMode: '',
            ccToken: '',
        }),
    }
)

export const userSchema = new schema.Entity(
    'users',
    { extended: extendedSchema, paymentMethods: [creditCardsSchema], accountCoupons: [accountCouponSchema] },
    {
        idAttribute: value => getNestedProp(value.users, 'userId'),
        processStrategy: value => {
            const {
                id,
                accountKey,
                users,
                addresses = [],
                extended = {},
                paymentMethods = [],
                accountCoupons = [],
                token,
                owner,
                padMethods = [],
                parkingIndividualDetail,
            } = value

            const billingAddress = addresses.find(address => address.type === 'BILLING') || {}
            const deliveryAddress = addresses.find(address => address.type === 'HOME') || {}

            const firstName = getNestedProp(users, 'firstName')
            const lastName = getNestedProp(users, 'lastName')

            return {
                id: getNestedProp(users, 'email'),
                accountKey: accountKey || id,
                email: getNestedProp(users, 'email'),
                name: `${firstName} ${lastName}`,
                paymentMethods,
                padMethods,
                creditCards: paymentMethods.map(card => card.id),
                accountCoupons,
                coupons: accountCoupons.map(coupon => coupon.couponCode),
                extended,
                accountId: parkingIndividualDetail.id,
                token,
                vehicles: get(extended, 'fieldMap.vehicles.fieldList', []).map(
                    vehicle => vehicle.fieldMap.vehicleId.value
                ),
                details: {
                    salutation: '',
                    firstName,
                    lastName,
                    phone: getNestedProp(owner?.contact?.mobile, 'value'),
                    emailAddress: getNestedProp(users, 'email'),
                    dateAdded: getNestedProp(users, 'dateAdded'),
                    address: {
                        address1: handleEmpty(billingAddress.line1),
                        address2: handleEmpty(billingAddress.line2),
                        city: handleEmpty(billingAddress.city),
                        state: handleEmpty(billingAddress.state),
                        postalCode: handleEmpty(billingAddress.zip).toUpperCase(),
                        country: handleEmpty(billingAddress.country),
                    },
                    billingAddress: {
                        firstName,
                        lastName,
                        address1: handleEmpty(billingAddress.line1),
                        address2: handleEmpty(billingAddress.line2),
                        city: handleEmpty(billingAddress.city),
                        state: handleEmpty(billingAddress.state),
                        postalCode: handleEmpty(billingAddress.zip).toUpperCase(),
                        country: handleEmpty(billingAddress.country),
                    },
                    deliveryAddress: {
                        firstName,
                        lastName,
                        address1: handleEmpty(deliveryAddress.line1),
                        address2: handleEmpty(deliveryAddress.line2),
                        city: handleEmpty(deliveryAddress.city),
                        state: handleEmpty(deliveryAddress.state),
                        postalCode: handleEmpty(deliveryAddress.zip).toUpperCase(),
                        country: handleEmpty(deliveryAddress.country),
                    },
                    excludeFields: '',
                    custom1: null,
                },
            }
        },
    }
)
